import type { Role } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { useGetRole, useListRoles } from '@sunstone/api-client/hooks';
import { useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { roleScopeLabel } from '@/lib/roles';

const PAGE_SIZE = 20;

export type RoleScope = 'System' | 'Tenant' | 'Platform';

export interface RolePickerItem {
  id: string;
  displayName: string;
  grantsAll: boolean;
  isUnrestricted: boolean;
  scope: RoleScope;
}

/**
 * Search+pagination-aware role picker state, shared by the invite wizard's
 * role field. Extracted (rather than inlined) because the resolved role is
 * consumed in three places (Who-step permission summary, the grantsAll
 * restriction gate, and the Confirm-step review) and has to stay correct
 * once search narrows the fetched page away from whatever's already
 * selected — the useGetRole fallback below is what keeps that true.
 */
export function useRolePicker(tenantId: string, token: string | null | undefined, roleId: string) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = useListRoles(tenantId ?? '', token, {
    search: debouncedSearch,
    pageSize: PAGE_SIZE,
  });
  const roles = data?.roles ?? [];
  const totalCount = data?.totalCount ?? 0;

  const items: RolePickerItem[] = roles.map((r) => ({
    id: r.id,
    displayName: r.name,
    grantsAll: r.grantsAll,
    isUnrestricted: r.isUnrestricted,
    scope: roleScopeLabel(r),
  }));

  const footer =
    totalCount > roles.length
      ? `Showing ${roles.length} of ${totalCount} — refine your search.`
      : undefined;

  const fromCurrentPage = roles.find((r) => r.id === roleId);
  const fallback = useGetRole(!fromCurrentPage && roleId ? roleId : '', token);
  const selectedRole: Role | undefined = fromCurrentPage ?? fallback.data?.role;

  return { search, setSearch, items, isLoading, footer, selectedRole };
}
